import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch } from "app/hooks";
import {
  setContent as setConfirmContent,
  setOpen as setConfirmDialogOpen,
  setOnChange as setConfirmOnChange,
  setOptions as setConfirmOptions,
  setTitle as setConfirmTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import { AMBIENT_SCRIBE_CUSTOM_TEMPLATES } from "constants/feature-flags";
import { ScribeTemplateTypeLabel } from "features/medical-scribe/components/scribe-template-type-label.component";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import FieldLabel from "features/staff-tasks-v2/drawer/staff-task-card/field-label.component";
import {
  AMBIENT_SCRIBE_TEMPLATE_SELECT_QUERY,
  useGenerateAfterVisitSummaryMutation,
  useGetAmbientScribeTemplatesQuery
} from "services/ambient-scribe";
import { AMBIENT_SCRIBE_TEMPLATE_TYPE } from "services/types/ambient-scribe-template.type";

/**
 * "" = let the backend resolve the template exactly as the automatic generation does:
 * appointment type -> practice default -> the seeded Suno letter.
 */
const RESOLVED_TEMPLATE_VALUE = "";
const RESOLVED_TEMPLATE_LABEL = "Default for this visit";

// `GET /templates/` defaults to a page size of 5, so a practice with more letter templates
// would silently lose entries from this picker.
const AVS_TEMPLATES_QUERY_PARAMS = {
  limit: 300,
  offset: 0,
  ordering: "-pk",
  query: AMBIENT_SCRIBE_TEMPLATE_SELECT_QUERY,
  type: AMBIENT_SCRIBE_TEMPLATE_TYPE.AFTER_VISIT_SUMMARY
};

type GenerateError =
  | { kind: "subscription"; error: string; message: string }
  | { kind: "generic" };

type AfterVisitSummaryGenerateSectionProps = {
  scribeId: number;
  /** Union of the local latch and `is_generating_after_visit_summary` from the scribe. */
  isGenerating: boolean;
  /** True once a letter exists, so the button reads **Apply** (same rule as the audiology one). */
  hasExistingSummary: boolean;
  /**
   * Controlled by the tab, not held here — the tab renders this section from two branches, so
   * React remounts it when the first letter arrives. `null` means "resolve like the automatic
   * run", which is a real choice.
   */
  selectedTemplateId: number | null;
  onSelectedTemplateIdChange: (id: number | null) => void;
  onGenerateStarted: () => void;
};

/**
 * The generate/apply row on the After Visit Summary tab — the letter sibling of
 * `AudiologySummaryGenerateSection`, differing in the mutation (the `after_visit_summary`
 * transform), the template type filter, and the empty option's meaning: with no explicit pick the
 * BACKEND resolves the template (appointment type → practice default → the seeded Suno letter),
 * the same path the automatic post-recording generation takes.
 */
export default function AfterVisitSummaryGenerateSection({
  scribeId,
  isGenerating,
  hasExistingSummary,
  selectedTemplateId,
  onSelectedTemplateIdChange,
  onGenerateStarted
}: Readonly<AfterVisitSummaryGenerateSectionProps>) {
  const theme = useTheme();
  const flags = useFlags();
  // "Users may choose templates" hides the PICKER, never the button — generating with the
  // resolved template is a first-class path.
  const canChooseTemplate = Boolean(flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES]);

  const [generateError, setGenerateError] = useState<GenerateError | null>(
    null
  );

  const { data: templates = [] } = useGetAmbientScribeTemplatesQuery(
    AVS_TEMPLATES_QUERY_PARAMS,
    { refetchOnMountOrArgChange: true, skip: !canChooseTemplate }
  );

  const [generateSummary, { isLoading: isRequestInFlight }] =
    useGenerateAfterVisitSummaryMutation();

  // A template deleted while the picker was open must not be sent.
  const templateId = useMemo(() => {
    if (selectedTemplateId == null) return null;
    return templates.some((t) => t.id === selectedTemplateId)
      ? selectedTemplateId
      : null;
  }, [selectedTemplateId, templates]);

  const isBusy = isGenerating || isRequestInFlight;

  const dispatch = useAppDispatch();

  const runGenerate = async () => {
    const result = await generateSummary({
      scribe_id: scribeId,
      ...(templateId != null
        ? { ambient_scribe_template_id: templateId }
        : undefined)
    });

    if ("error" in result) {
      const err = result.error as
        | {
            status?: number;
            data?: {
              error?: string;
              message?: string;
              subscription_state?: string;
            };
          }
        | undefined;

      // 409 is "one is already running for this scribe" (likely the automatic generation), not a
      // failure — the lock is per scribe and self-heals. Show the spinner.
      if (err?.status === 409) {
        onGenerateStarted();
        return;
      }
      if (err?.status === 402 && err?.data?.subscription_state) {
        setGenerateError({
          kind: "subscription",
          error: err.data.error ?? "Subscription required",
          message: err.data.message ?? ""
        });
        return;
      }
      setGenerateError({ kind: "generic" });
      return;
    }

    onGenerateStarted();
  };

  const handleApply = () => {
    // A new generation writes a NEW note row, so a correction staff saved on the current letter
    // stops being the letter anyone sees. Confirm before replacing it — on this surface the button
    // reads "Apply" and sits next to a template picker, so "I'm switching template" and "I'm
    // discarding my edits" are otherwise the same click.
    if (!hasExistingSummary) {
      void runGenerate();
      return;
    }
    dispatch(setConfirmTitle("Replace this letter?"));
    dispatch(
      setConfirmContent(
        "A new letter will be written from the visit recording and will replace the one shown here. Any edits saved to the current letter will not carry over."
      )
    );
    dispatch(
      setConfirmOptions({
        actionButtonText: "Replace letter",
        // The shared confirm defaults to `snackbar + 2`, which is BELOW the scribe details modal
        // (`snackbar + 101`) — it would open unreachably behind it. Same layer every other nested
        // dialog in this modal uses.
        zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar)
      })
    );
    dispatch(setConfirmOnChange(() => void runGenerate()));
    dispatch(setConfirmDialogOpen(true));
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems={canChooseTemplate ? "flex-end" : "center"}
        flexWrap="wrap"
      >
        {canChooseTemplate && (
          <Stack>
            <FieldLabel
              label="Select Letter Template"
              sx={{ mb: "3px", mt: -4 }}
            />
            {/* Not disabled mid-run on purpose — the button carries the real guard. */}
            <FormControl size="small" sx={{ minWidth: 280, maxWidth: 400 }}>
              <Select
                id="after-visit-summary-template-select"
                data-testid="after-visit-summary-template-select"
                value={templateId ?? RESOLVED_TEMPLATE_VALUE}
                displayEmpty
                sx={{
                  "& .MuiOutlinedInput-notchedOutline legend": {
                    display: "none"
                  },
                  "& .MuiOutlinedInput-notchedOutline": { top: 0 }
                }}
                onChange={(e) => {
                  const next = e.target.value;
                  onSelectedTemplateIdChange(
                    next === RESOLVED_TEMPLATE_VALUE ? null : Number(next)
                  );
                }}
                MenuProps={{
                  sx: { zIndex: 9999 },
                  PaperProps: { sx: { zIndex: 9999, maxHeight: 360 } }
                }}
                renderValue={(selected) =>
                  selected === RESOLVED_TEMPLATE_VALUE
                    ? RESOLVED_TEMPLATE_LABEL
                    : (templates.find((t) => t.id === Number(selected))?.name ??
                      "")
                }
              >
                <MenuItem value={RESOLVED_TEMPLATE_VALUE}>
                  {RESOLVED_TEMPLATE_LABEL}
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem
                    key={template.id}
                    value={template.id}
                    sx={{ alignItems: "flex-start", py: 1 }}
                  >
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body3" fontWeight={700}>
                        {template.name}
                      </Typography>
                      <ScribeTemplateTypeLabel
                        type={template.type}
                        variant="caption"
                        muted
                      />
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}

        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleApply}
          loading={isBusy}
          disabled={isBusy}
          size="small"
          data-testid="generate-after-visit-summary-button"
        >
          {hasExistingSummary ? "Apply" : "Generate Letter"}
        </LoadingButton>
      </Stack>

      <MuiDialog
        open={generateError !== null}
        onClose={() => setGenerateError(null)}
        sx={{ zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar) }}
      >
        <DialogTitle>
          {generateError?.kind === "subscription"
            ? "Subscription Required"
            : "Generation Failed"}
        </DialogTitle>
        <DialogContent>
          {generateError?.kind === "subscription" ? (
            <Alert severity="warning">
              <AlertTitle>{generateError.error}</AlertTitle>
              {generateError.message}
            </Alert>
          ) : (
            <Typography>
              Something went wrong while processing your request. Please try
              again in a few moments. If the issue persists, contact Suno
              support for assistance.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateError(null)}>Close</Button>
        </DialogActions>
      </MuiDialog>
    </>
  );
}
